import { IIncomeEntry } from "types";
import { Box, CircularProgress, Typography } from "@mui/material";
import IncomeEntryEdit from "components/IncomeEntry/Detail/IncomeEntryEdit";
import IncomeEntryConfirmation from "components/IncomeEntry/Detail/IncomeEntryConfirmation";
import IncomeEntryArticle from "components/IncomeEntry/Article/IncomeEntryArticle";
import { INCOME_ENTRY_STATUS, ROUTES, STATUS_CODES } from "constants/index";
import { INCOME_ENTRIES_URL } from "constants/urls";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";

export default function IncomeEntryDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {showSnackBar} = useToast();
  const { 
    fetchData,
    updateData,
  } = useCrud<IIncomeEntry>(INCOME_ENTRIES_URL);

  const [incomeEntryData, setIncomeEntryData] = useState<IIncomeEntry | null>(null);
  const [stepValue, setStepValue] = useState<"main" | "articles" | "confirmation">("main");
  const [fetchingData, setFetchingData] = useState(false);
  const [savingData, setSavingData] = useState(false);

  useEffect(() => {
    if(!id){
      navigate(ROUTES.HOME);
      return;
    }
    setFetchingData(true);
    fetchData(id)
      .then((r: {data: IIncomeEntry | null; status: number}) => {
        if(r.status === 200 && (r.data?.status === INCOME_ENTRY_STATUS.FINISHED || r.data?.status === INCOME_ENTRY_STATUS.VERIFIED)) {
          showSnackBar("El ingreso ya ha sido finalizado o verificado", "info");
          navigate(ROUTES.INCOME_ENTRY);
          return;
        }
        setIncomeEntryData(r.data?? null);
      })
      .finally(() => setFetchingData(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const saveIncomeEntryChanges = useCallback(async (updatedIncomeEntry: IIncomeEntry, nextFn: () => void) => {
    if (!updatedIncomeEntry?.id) return;
    setSavingData(true);
    try {
      const response = await updateData(updatedIncomeEntry.id, updatedIncomeEntry, true, true);
      if (response?.status !== STATUS_CODES.OK) {
        const message = response?.data?.message;
        throw new Error(typeof message === "string" && message.trim() ? message : "Error al guardar los cambios");
      }
      setIncomeEntryData(response.data as IIncomeEntry ?? null);
      nextFn();
    } catch (error) {
      showSnackBar(error instanceof Error ? error.message : "Error al guardar los cambios", "error");
      if (stepValue === "main") navigate(ROUTES.INCOME_ENTRY);
    } finally {
      setSavingData(false);
    }
  }, [updateData, stepValue, navigate, showSnackBar]);

  const nextStep = async (updatedValue?: IIncomeEntry) => {
    let nextIncomeEntryData: IIncomeEntry = {
      ...incomeEntryData!,
      ...updatedValue,
      status: INCOME_ENTRY_STATUS.IN_PROGRESS,
    };
    switch(stepValue) {
      case "main":
        nextIncomeEntryData = {
          ...nextIncomeEntryData,
          status: INCOME_ENTRY_STATUS.IN_PROGRESS,
        };
        await saveIncomeEntryChanges(nextIncomeEntryData, () => setStepValue("articles"));
        break;
      case "articles":
        await saveIncomeEntryChanges(nextIncomeEntryData, () => setStepValue("confirmation"));
        break;
      case "confirmation":
        nextIncomeEntryData = {
          ...nextIncomeEntryData,
          status: INCOME_ENTRY_STATUS.VERIFIED
        }
        await saveIncomeEntryChanges(nextIncomeEntryData, () => navigate(ROUTES.INCOME_ENTRY));
        break;
      default:
        setStepValue("main");
    }
  }

  if (fetchingData) {
    return (
      <Box
        width={"100%"}
        height={"100%"}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pt: 4,
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2">Obteniendo remito...</Typography>
      </Box>
    );
  }

  if(!incomeEntryData) {
    return (
      <Box
        width={"100%"}
        height={"100%"}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pt: 4,
          gap: 2,
        }}
      >
        <Typography variant="body2">No se pudo obtener el remito.</Typography>
      </Box>
    );
  }

  return (
    <Box width={"100%"} height={"100%"} position={"relative"}>
      {stepValue === "main" && (<>
        <IncomeEntryEdit 
          incomeEntry={incomeEntryData}
          setIncomeEntry={setIncomeEntryData}
          nextStep={nextStep}
          loading={savingData}
        />
      </>)}

      {stepValue === "articles" && (
        <IncomeEntryArticle
          incomeEntryData={incomeEntryData}
          setIncomeEntryData={setIncomeEntryData}
          nextStep={nextStep}
          loading={savingData}
        />
      )}

      {stepValue === "confirmation" && ( <>
        <IncomeEntryConfirmation
          incomeEntry={incomeEntryData}
          nextStep={nextStep}
          loading={savingData}
        />
      </>)}
    </Box>
  );
}
